import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import {
  FieldValues,
  Path,
  UnpackNestedValue,
  useForm,
  UseFormProps,
  UseFormReturn,
  UseFormSetError,
} from 'react-hook-form';
import { ServerValidationError } from '../api/axios';

export type AsyncSubmitHandler<TFieldValues extends FieldValues> = (
  data: UnpackNestedValue<TFieldValues>,
  event?: React.BaseSyntheticEvent,
) => Promise<any>;

export function wrapWithServerErrorHandling<TFieldValues extends FieldValues>(
  onSubmit: AsyncSubmitHandler<TFieldValues>,
  setError: UseFormSetError<TFieldValues>,
  onNonValidationError?: (e: unknown) => void,
): AsyncSubmitHandler<TFieldValues> {
  return (data, event) =>
    onSubmit(data, event).catch((e) => {
      if (e instanceof ServerValidationError) {
        e.errors.forEach((e) => {
          setError(e.param as Path<TFieldValues>, { message: e.msg });
        });
      } else if (onNonValidationError) {
        onNonValidationError(e);
      } else {
        return Promise.reject(e);
      }
    });
}

export function useFormWithServerErrorHandling<
  TFieldValues extends FieldValues = FieldValues,
  TContext = any,
>(props?: UseFormProps<TFieldValues, TContext>): UseFormReturn<TFieldValues, TContext> {
  const { handleSubmit, setError, ...rest } = useForm(props);
  const { enqueueSnackbar } = useSnackbar();

  const handleNonValidationError = useCallback(() => {
    enqueueSnackbar('Something went wrong', { variant: 'error' });
  }, [enqueueSnackbar]);

  return {
    ...rest,
    setError,
    handleSubmit: (onSubmit) =>
      handleSubmit(wrapWithServerErrorHandling(onSubmit, setError, handleNonValidationError)),
  };
}
